import { orderBy } from "lodash";
import { CandidateViewModel } from "../../model/CandidateViewModel";
import { CandidateCourseViewModel } from "../../model/courses/CandidateCourseViewModel";
import { ApiResponse } from "../CustomFetch";
import { api, transformApiResponse } from "./BaseApi";

const getCandidateUrl = (id?: string) => "/Candidate" + (id === undefined ? "" : `/${id}`);

const candidatesApi = api.enhanceEndpoints({ addTagTypes: ["Candidates", "CandidateCourses"] }).injectEndpoints({
  endpoints: (builder) => ({
    getCandidates: builder.query<CandidateViewModel[], void>({
      query: () => getCandidateUrl(),
      transformResponse: (response: ApiResponse<CandidateViewModel[]>) =>
        orderBy(response.data, (candidate) => candidate.lastName.toLowerCase()),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: "Candidates" as const, id })), { type: "Candidates", id: "LIST" }]
          : [{ type: "Candidates", id: "LIST" }],
    }),
    getCandidate: builder.query<CandidateViewModel, string>({
      query: getCandidateUrl,
      transformResponse: (response: ApiResponse<CandidateViewModel>) => response.data,
      providesTags: (_result, _error, arg) => [{ type: "Candidates", id: arg }],
    }),
    deleteCandidate: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: getCandidateUrl(id),
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Candidates", id: "LIST" }],
    }),
    createCandidate: builder.mutation<ApiResponse<void>, CandidateViewModel>({
      query: (candidate: CandidateViewModel) => ({
        url: getCandidateUrl(),
        body: candidate,
        headers: { "Content-Type": "application/json" },
        method: "POST",
      }),
      invalidatesTags: [{ type: "Candidates", id: "LIST" }],
    }),
    updateCandidate: builder.mutation<ApiResponse<void>, CandidateViewModel>({
      query: (candidate: CandidateViewModel) => ({
        url: getCandidateUrl(),
        body: candidate,
        headers: { "Content-Type": "application/json" },
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Candidates", id: "LIST" },
        { type: "Candidates", id: arg.id },
      ],
    }),
    exportDebtorNumber: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `${getCandidateUrl(id)}/exportDebtorNumber`,
        method: "POST",
      }),
    }),

    getCandidateCoursesInternal: builder.query<CandidateCourseViewModel[], string>({
      query: (id) => `${getCandidateUrl(id)}/courses`,
      transformResponse: transformApiResponse,
      providesTags: (_result, _error, arg) => [{ type: "CandidateCourses", id: arg }],
    }),

    getCandidatesValid: builder.query<CandidateViewModel[], void>({
      query: () => `${getCandidateUrl()}/valid`,
      transformResponse: (response: ApiResponse<CandidateViewModel[]>) =>
        orderBy(response.data, (candidate) => candidate.lastName.toLowerCase()),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: "Candidates" as const, id })), { type: "Candidates", id: "LIST" }]
          : [{ type: "Candidates", id: "LIST" }],
    }),
  }),
});

export const {
  endpoints,
  useGetCandidatesQuery,
  useGetCandidateQuery,
  useDeleteCandidateMutation,
  useCreateCandidateMutation,
  useUpdateCandidateMutation,
  useExportDebtorNumberMutation,
  useGetCandidateCoursesInternalQuery,
  useGetCandidatesValidQuery,
} = candidatesApi;
